import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Typography, Switch } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import { useSnackbar, usePagination, useApi } from 'app/hooks';

const useStyles = makeStyles(theme => ({
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    switch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.type === 'dark' ? '#bdbdbd' : undefined
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.type === 'dark' ? '#757575' : undefined
        }
    },
    toggleContent: {
        padding: theme.spacing(3),
        textAlign: 'left'
    }
}));

const INITIAL_FORM = {
    channelId: '',
    metaAppId: ''
};

function ChannelAppsTab() {
    const classes = useStyles();
    const theme = useTheme();

    // Hooks
    const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
    const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(10);
    const { isLoading, request } = useApi();

    // State
    const [data, setData] = useState([]);
    const [channels, setChannels] = useState([]);
    const [metaApps, setMetaApps] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [toggleOpen, setToggleOpen] = useState(false);

    // Filter state
    const [filterValues, setFilterValues] = useState({
        channelId: '',
        status: ''
    });
    const [refreshKey, setRefreshKey] = useState(0);

    // Fetch channels for dropdown
    const fetchChannels = () => {
        request({
            module: 'saChannels',
            method: 'getChannels',
            payload: {},
            onSuccess: (response) => {
                const channelsList = response.data.data.channels || [];
                setChannels(channelsList);
            },
            onError: (message) => showError(message || 'Error fetching channels')
        });
    };

    // Fetch meta apps for dropdown
    const fetchMetaApps = () => {
        request({
            module: 'saChannels',
            method: 'metaAppList',
            payload: {},
            onSuccess: (response) => {
                const apps = response.data.data.data || [];
                setMetaApps(apps);
            },
            onError: (message) => showError(message || 'Error fetching meta apps')
        });
    };

    // Fetch channel apps
    const fetchChannelApps = () => {
        const payload = {};
        if (filterValues.channelId) payload.channelId = filterValues.channelId;
        if (filterValues.status) payload.status = filterValues.status;

        request({
            module: 'saChannels',
            method: 'channelsAppList',
            payload,
            onSuccess: (response) => {
                const apps = response.data.data.data || [];
                setData(apps);
                setTotalItems(response.data.data.total || apps.length);
            },
            onError: (message) => showError(message || 'Error fetching channel apps')
        });
    };

    useEffect(() => {
        fetchChannels();
        fetchMetaApps();
    }, []);

    useEffect(() => {
        fetchChannelApps();
    }, [filterValues, refreshKey]);

    // Channel options for dropdown
    const channelOptions = channels.map(ch => ({
        value: ch.id.toString(),
        label: ch.name
    }));

    // Meta app options for dropdown
    const metaAppOptions = metaApps.map(app => ({
        value: app.id.toString(),
        label: app.name
    }));

    // Form handlers
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setFormData(INITIAL_FORM);
    };

    // Build API payload from form
    const buildPayload = () => {
        if (!formData.channelId || !formData.metaAppId) {
            showWarning('Please select both Channel and Meta App');
            return null;
        }
        return {
            channelId: formData.channelId,
            metaAppId: formData.metaAppId
        };
    };

    // Create
    const handleCreate = () => {
        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'channelsAppAdd',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'Channel app created successfully');
                setCreateOpen(false);
                resetForm();
                fetchChannelApps();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error creating channel app');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Edit open
    const handleEditOpen = (row) => {
        setSelectedRow(row);
        setFormData({
            channelId: row.channel_id?.toString() || '',
            metaAppId: row.meta_app_id?.toString() || ''
        });
        setEditOpen(true);
    };

    // Update
    const handleUpdate = () => {
        const payload = buildPayload();
        if (!payload) return;

        payload.id = selectedRow.id;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'channelsAppUpdate',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'Channel app updated successfully');
                setEditOpen(false);
                resetForm();
                setSelectedRow(null);
                fetchChannelApps();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error updating channel app');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Toggle - open confirmation
    const handleToggleOpen = (row) => {
        setSelectedRow(row);
        setToggleOpen(true);
    };

    // Toggle - confirm
    const handleToggleConfirm = () => {
        setTogglingId(selectedRow.id);
        setToggleOpen(false);
        request({
            module: 'saChannels',
            method: 'channelsAppToggle',
            payload: { id: selectedRow.id },
            onSuccess: (response) => {
                const msg = response.data.data.message || 'Channel app toggled successfully';
                showSuccess(msg);
                fetchChannelApps();
                setTogglingId(null);
                setSelectedRow(null);
            },
            onError: (message) => {
                showError(message || 'Error toggling channel app');
                setTogglingId(null);
                setSelectedRow(null);
            },
            onFinally: () => setTogglingId(null)
        });
    };

    const isFormInvalid = () => {
        return !formData.channelId || !formData.metaAppId || saving;
    };

    // Filter handlers
    const handleFiltersApply = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleFiltersClear = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Filter fields config
    const filterFields = [
        {
            name: 'channelId',
            label: 'Channel',
            type: 'select',
            options: [
                { value: '', label: 'All Channels' },
                ...channelOptions
            ],
            defaultValue: ''
        },
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
            ],
            defaultValue: ''
        }
    ];

    // Table columns
    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        // { id: 'channel_id', numeric: false, disablePadding: false, label: 'Channel Id' },
        { id: 'meta_app_id', numeric: false, disablePadding: false, label: 'Meta App Id' },
        {
            id: 'channel_code',
            numeric: false,
            disablePadding: false,
            label: 'Channel Code',
            tooltip: true
        },
        {
            id: 'channel_name',
            numeric: false,
            disablePadding: false,
            label: 'Channel Name',
            tooltip: true
        },
        { id: 'app_name', numeric: false, disablePadding: false, label: 'App Name', tooltip: true },
        { id: 'app_id', numeric: false, disablePadding: false, label: 'App ID', tooltip: true },
        { id: 'app_version', numeric: false, disablePadding: false, label: 'App Version' },
        {
            id: 'webhook_url', numeric: false, disablePadding: false, label: 'Webhook URL', msg: true, truncateLength: 30,
        },
        {
            id: 'status',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            pill: true,
            pillConfig: {
                mapping: {
                    active: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    inactive: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        {
            id: 'app_status',
            numeric: false,
            disablePadding: false,
            label: 'App Status',
            pill: true,
            pillConfig: {
                mapping: {
                    active: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    inactive: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
        { id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
        { id: 'edit', numeric: false, disablePadding: false, label: 'Edit' },
        {
            id: 'toggle',
            numeric: false,
            disablePadding: false,
            label: 'Active',
            customRow: row => (
                <Switch
                    checked={row.status === 'active'}
                    onChange={() => handleToggleOpen(row)}
                    disabled={togglingId === row.id}
                    className={classes.switch}
                    size="small"
                />
            )
        }
    ];

    // Form content for create/edit
    const renderFormContent = () => (
        <div className={classes.formContent}>
            <FormFieldOmni
                label="Channel *"
                variant="outlined"
                value={formData.channelId}
                onChange={(e) => handleFormChange('channelId', e.target.value)}
                type="select"
                options={channelOptions}
                size="small"
                fullWidth
                placeholder="Select channel"
            />
            <FormFieldOmni
                label="Meta App *"
                variant="outlined"
                value={formData.metaAppId}
                onChange={(e) => handleFormChange('metaAppId', e.target.value)}
                type="select"
                options={metaAppOptions}
                size="small"
                fullWidth
                placeholder="Select meta app"
            />
        </div>
    );

    // Create dialog actions
    const createActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setCreateOpen(false); resetForm(); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleCreate} variant="outlined" label={saving ? 'Creating...' : 'Create'} disabled={isFormInvalid()} />
        </div>
    );

    // Edit dialog actions
    const editActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleUpdate} variant="outlined" label={saving ? 'Updating...' : 'Update'} disabled={isFormInvalid()} />
        </div>
    );

    return (
        <div className="w-full mx-auto">
            {/* Filters, Export & Create */}
            <DateFilterWithExport
                showDateRange={false}
                showExport={false}
                filterFields={filterFields}
                filterValues={filterValues}
                onFiltersApply={handleFiltersApply}
                onFiltersClear={handleFiltersClear}
                showRefresh={true}
                onRefresh={handleRefresh}
                customRightContent={
                    <ButtonOmniNew
                        buttonType="create"
                        label="Add Channel App"
                        onClick={() => { resetForm(); setCreateOpen(true); }}
                        variant="outlined"
                    />
                }
            />

            {/* Table */}
            <TableOmniNewAnimated
                rows={data}
                headCells={headCells}
                perPageRows={currentParams.limit}
                setCurrentParams={setCurrentParams}
                totalItems={totalItems}
                curPage={currentParams.page}
                loading={isLoading}
                onEditClick={handleEditOpen}
            />

            {/* Create Dialog */}
            <ModalOmni
                disableBackdropClick
                open={createOpen}
                handleClose={() => { setCreateOpen(false); resetForm(); }}
                title="Add Channel App"
                content={renderFormContent()}
                actions={createActions}
                customWidth={500}
            />

            {/* Edit Dialog */}
            <ModalOmni
                disableBackdropClick
                open={editOpen}
                handleClose={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }}
                title="Edit Channel App"
                content={renderFormContent()}
                actions={editActions}
                customWidth={500}
            />

            {/* Toggle Confirmation Dialog */}
            <ModalOmni
                disableBackdropClick
                open={toggleOpen}
                handleClose={() => { setToggleOpen(false); setSelectedRow(null); }}
                title={selectedRow?.status === 'active' ? 'Deactivate Channel App' : 'Activate Channel App'}
                content={
                    <div className={classes.toggleContent}>
                        <Typography variant="body1" color="textPrimary">
                            Are you sure you want to {selectedRow?.status === 'active' ? 'deactivate' : 'activate'} the channel app "<strong>{selectedRow?.app_name || ''}</strong>"?
                        </Typography>
                    </div>
                }
                actions={
                    <div className={classes.buttonContainer}>
                        <ButtonOmni
                            onClick={() => { setToggleOpen(false); setSelectedRow(null); }}
                            variant="outlined"
                            label="Cancel"
                        />
                        <ButtonOmni
                            onClick={handleToggleConfirm}
                            variant="outlined"
                            label={selectedRow?.status === 'active' ? 'Deactivate' : 'Activate'}
                        />
                    </div>
                }
                customWidth={450}
            />

            <SnackbarOmni {...snackbar} />
        </div>
    );
}

export default React.memo(ChannelAppsTab);